import {createCipheriv, randomBytes, createHash} from "crypto";

export interface EncryptedPackage
{
    key: string,
    iv: string,
    cipherText: string,
    auth_tag: string
}

/**
 * Encrypts a text input using a randomly generated key and IV using aes
 * @param text the text to encrypt
 * @returns An Encrypted Package consisting of the key, IV, auth_tag, and cipherText, in hex format
 */
export const encryptMessage = (text: string): EncryptedPackage =>
{
    const algorithm = "aes-256-gcm";
    const key = randomBytes(32);
    const iv = randomBytes(16);
    const cipher = createCipheriv(algorithm, key, iv);
    let encrypted = cipher.update(text, "utf8", "hex");
    encrypted += cipher.final("hex");
    return { key: key.toString("hex"), iv: iv.toString("hex"), cipherText: encrypted, auth_tag: cipher.getAuthTag().toString("hex") };
}


export interface EncryptedRequestResponse
{
    initialResponse: any,
    ackResponse?: any,
    payment_id?: string
}

/**
 * Sends an encrypted request to the indicated URL. After sending the initial request, the encryption key will be encrypted
 * using the RSA public key provided and sent to the payment_ack endpoint.
 * @param payload The portion of the request to encrypt. Will be sent to server as a Form Data field named 'encrypted'.
 * @param unEncryptedData The portion of the request to send, unencrypted, along with the request. Will be sent to the
 * server as a FormData field with the name 'unencrypted'.
 * @param url The URL to send the request to
 * @param initParams The initParams for the initial request.
 * @param pubKey The public key to encrypt the message's encryption key with for the 2nd request to the payment_ack endpoint.
 * @returns An EncryptedRequestResponse with both responses (if both were made) and the payment_id (if the first request was successful.)
 */
//TODO: Since the return value of response.json() is an any, for now this is a promise of any.
// We should make this a generic (though even that may be difficult if we have a different type for success vs. failure.)
export const sendEncryptedRequest = (payload: any, unEncryptedData: any, url: URL, initParams: RequestInit, pubKey: string): Promise<EncryptedRequestResponse> =>
{
    const encrypted = encryptMessage(JSON.stringify(payload));

    const encryptedRequest = new FormData();
    encryptedRequest.append('unencrypted', JSON.stringify(unEncryptedData));
    encryptedRequest.append('iv', encrypted.iv);
    encryptedRequest.append('auth_tag', encrypted.auth_tag);
    encryptedRequest.append('encrypted', encrypted.cipherText);

    return sendEncryptedPacket(encryptedRequest, url, initParams)
        .then(response => response.json())
        .then((initRes) => 
        {
            if(initRes.status == 'success')
            {
                const payment_id = initRes.payment_id;
                console.log('Payment ID: ' + payment_id);
                console.log("Sending Payment Ack...");
                //TODO: Assume initParams are the same for both messages.
                return sendPaymentAck(encrypted.key, payment_id, initParams, pubKey)
                    .then(response => response.json())
                    .then((res) => {
                        return {
                            initialResponse: initRes,
                            ackResponse: res,
                            payment_id: payment_id
                        };
                    })
            }
            else
            {
                console.log('Failure when sending encrypted packet: ' + initRes);
                return {
                    initialResponse: initRes
                };
            }
        })
}





// Browser-safe hex conversion
function uint8ArrayToHex(bytes: Uint8Array): string
{
  return Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join('');
}




//Transform public key into a CryptoKey Object to be used for encryption
async function importPublicKey(pem: string): Promise<CryptoKey> 
{
    const pemHeader = "-----BEGIN PUBLIC KEY-----";
    const pemFooter = "-----END PUBLIC KEY-----";
    const pemContents = pem
        .replace(pemHeader, "")
        .replace(pemFooter, "")
        .replace(/\n/g, "")
        .trim();

    const binaryDer = Uint8Array.from(atob(pemContents), c => c.charCodeAt(0));

    return crypto.subtle.importKey(
        "spki",
        binaryDer,
        {
        name: "RSA-OAEP",
        hash: "SHA-256",
        },
        true,
        ["encrypt"]
    );
}




/**
 * Encrypt a message with RSA, using the public key provided
 * @param publicKeyPem A string with the public key
 * @param plaintext The text to be encrypted
 * @returns The cipherText, in hex
 */
async function encryptWithPublicKey(publicKeyPem: string, plaintext: string): Promise<string>
{
    // 1. Import the public key
    const publicKey = await importPublicKey(publicKeyPem);

    // 2. Encode the plaintext
    const encoder = new TextEncoder();
    const data = encoder.encode(plaintext);

    // 3. Encrypt (RSA-OAEP + SHA-256)
    const encrypted = await crypto.subtle.encrypt(
        {
            name: "RSA-OAEP",
        },
        publicKey,
        data
    );

    return(uint8ArrayToHex(new Uint8Array(encrypted)));
}


/**
 * Encrypts the encryption key provided with the RSA public key provided, and sends the result to the payment_ack
 * endpoint, with the initParamsProvided (except as necessary to facililtate the payment_ack request.)
 * @param key The encryption key used to encrypt the encryped portion of the payment being facilitated.
 * @param payment_id The payment ID of the payment being facilitated
 * @param initParams The initParams to use for the request..
 * @param pubKey The Public key to use to encrypt the encryption key for sending to the request.
 * @returns The response from the endpoint, or a constructed error response for a failure during encryption.
 */
const sendPaymentAck =  async (key: string, payment_id: string, initParams: RequestInit, pubKey: string): Promise<Response> =>
{
    console.log("Encrypting Encryption Key...");

    try
    {
        const cipher = await encryptWithPublicKey(pubKey, key);

        console.log('Sending Encryption Key...');

        const keyRequestBody = 
        {
            id: payment_id,
            cipherText: cipher,
            cipherHash: createHash("sha256").update(cipher).digest("hex")
        }

        const fullInitParams = 
        {
            ...initParams,
            headers: {...initParams.headers, 'Content-Type': `application/json`},
            body: JSON.stringify(keyRequestBody)
        };
        return fetch(process.env.REACT_APP_REFLEBULA + '/a1/tga/payment_ack', fullInitParams)
    }
    catch (error: any) 
    {
        // Handle specific encryption failures
        console.error("Encryption failed:", error.message);
        return(Promise.resolve(new Response(JSON.stringify({status: 'failure', reason: 'An internal error occured when sending your payment.'}), { status: 500, statusText: "TestEncryptionKeyResponse" })));
    }
}





/**
 * Send the formData provided to the target URL using the initParams provided
 * @param formData The form data to send
 * @param url the URL to send the packet to
 * @param initParams the initParams to use (save for the body)
 * @returns The response from the request
 */
const sendEncryptedPacket =  async (formData: any, url: URL, initParams: RequestInit): Promise<Response> =>
{
    const fullInitParams = 
    {
        ...initParams,
        body: formData
    };

    console.log('Sending Encrypted Packet...');

    return fetch(url, fullInitParams);
}





export function strToIV(iv_text: string)
{
  const buf = Buffer.from(iv_text, 'utf8'); // Encode as UTF‑8

  // Make sure it is exactly 16 bytes: pad with zeros or truncate
  if (buf.length === 16) return buf;
  if (buf.length > 16) return buf.subarray(0, 16);

  const iv = Buffer.alloc(16);
  buf.copy(iv);
  return iv;
}
